import {
  type RiskGapStatus,
  riskGapDefaultSubstatuses,
} from '@ctw/shared/api/fhir/models/risk-gap-v2';
import { Button } from '@ctw/shared/components/button';
import { Heading } from '@ctw/shared/components/heading';
import { TagCheckboxGroup } from '@ctw/shared/components/inputs/tag-checkbox-group';
import { tw } from '@ctw/shared/utils/tailwind';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';

/** User statuses that have dedicated dialogs in the form (Accept, Dismiss, Snooze/Pending). */
export type RiskGapStatusDialogStatus =
  | 'open-gap'
  | 'accepted-for-review'
  | 'pending'
  | 'dismissed';

const STATUS_TAG_OPTIONS: Array<{ value: RiskGapStatusDialogStatus; label: string }> = [
  { value: 'open-gap', label: 'Open' },
  { value: 'pending', label: 'Pending' },
  { value: 'accepted-for-review', label: 'Accept' },
  { value: 'dismissed', label: 'Dismiss' },
];

export interface RiskGapStatusDialogContentProps {
  /** The initial note value. Does not change once the component mounts. */
  note: string;
  onConfirm: (
    pendingStatus: RiskGapStatusDialogStatus,
    substatuses: Array<string>,
    pendingNote: string,
  ) => void | Promise<void>;
  onCancel: () => void;
  status: RiskGapStatusDialogStatus;
  selectedSubstatuses?: Array<string>;
}

/**
 * Dialog content used within the StatusDialogModal.
 * Triggered as a modal within the risk-gap-detail-view and risk-gap-status-form-v2.
 */
export function RiskGapStatusDialogContent({
  status,
  note,
  selectedSubstatuses = [],
  onConfirm,
  onCancel,
}: RiskGapStatusDialogContentProps) {
  const [substatuses, setSubstatuses] = useState<Array<string>>(selectedSubstatuses);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingNote, setPendingNote] = useState(note);
  const [pendingStatus, setPendingStatus] = useState<RiskGapStatusDialogStatus>(status);

  const substatusOptions = riskGapDefaultSubstatuses[pendingStatus as RiskGapStatus] ?? [];

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await Promise.resolve(onConfirm(pendingStatus, substatuses, pendingNote));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={tw`w-[372px] space-y-6`}>
      <div className={tw`space-y-2`}>
        <Heading level="h2" className={tw`font-semibold text-lg`}>
          Update Status
        </Heading>
      </div>

      <TagCheckboxGroup
        legend="Status"
        name="risk-gap-status-dialog-status"
        options={STATUS_TAG_OPTIONS}
        selectedOptions={[pendingStatus]}
        onOptionSelected={(option) => {
          setPendingStatus(option.value as RiskGapStatusDialogStatus);
          setSubstatuses(option.value === status ? selectedSubstatuses : []);
        }}
        onOptionDeselected={(option) => {
          if (option.value === pendingStatus) {
            setPendingStatus(status);
            setSubstatuses(selectedSubstatuses);
          }
        }}
      />

      {!isEmpty(substatusOptions) && (
        <TagCheckboxGroup
          legend="Reason (optional - select any that apply)"
          name={`risk-gap-status-dialog-substatus-${pendingStatus}`}
          options={[...substatusOptions]}
          selectedOptions={substatuses}
          onOptionSelected={(option) => {
            if (!substatuses.includes(option.value)) {
              setSubstatuses((prev) => [...prev, option.value]);
            }
          }}
          onOptionDeselected={(option) => {
            setSubstatuses((prev) => prev.filter((v) => v !== option.value));
          }}
        />
      )}
      <div>
        <label
          htmlFor="risk-gap-status-dialog-note"
          className={tw`text-meta-main text-xs uppercase`}
        >
          Notes (Optional)
          <textarea
            id="risk-gap-status-dialog-note"
            placeholder={'Add notes...'}
            onChange={(event) => setPendingNote(event.currentTarget.value)}
            value={pendingNote}
            rows={4}
            className={tw`mt-1 w-full resize-none border border-meta-background text-content-main shadow-none`}
          />
        </label>
      </div>
      <div className={tw`flex justify-end gap-3`}>
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          className={tw`border-none bg-transparent shadow-none hover:bg-transparent`}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={() => void handleConfirm()}
          disabled={isSubmitting}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
